<template>
  <button :class="{ desktop: $isDesktop }">
    <img v-if="image" :src="image" class="img" loading="lazy" />
    <div v-else class="img" />
    <div class="card-info">
      {{ getWishlistDisplayName() }}
      <translate
        v-if="guest && wishlist?.ownerFirstName"
        class="owner"
        :tt="{
          ownerFirstName: wishlist.ownerFirstName,
          ownerLastName: wishlist.ownerLastName,
        }"
      >
        By {ownerFirstName} {ownerLastName}
      </translate>
      <div>
        <translate v-if="showCount" class="count" :tt="{ count }">
          {count} sites
        </translate>
        <template v-if="status && !guest">
          <translate v-if="status === 'public'" class="status">
            Public
          </translate>
          <translate v-else-if="status === 'private'" class="status">
            Private
          </translate>
        </template>
        <translate v-else-if="guest" class="guest"> Guest </translate>
      </div>
    </div>
  </button>
</template>

<script setup lang="ts">
import { Wishlist } from '../../types'

const props = withDefaults(
  defineProps<{
    id?: string
    name: string
    isMain?: boolean
    image?: string
    count?: number
    status?: string
    showCount?: boolean
    guest?: boolean
    wishlist?: Wishlist
  }>(),
  {
    id: undefined,
    count: 0,
    isMain: false,
    showCount: true,
    status: 'private',
    image: undefined,
    guest: false,
    wishlist: undefined,
  },
)

const i18n = useI18n()

function getWishlistDisplayName() {
  return props.isMain ? i18n.t('My favourites') : props.name
}
</script>
<style scoped>
button {
  align-items: flex-start;
  border: 1px solid var(--colour-page-background);
  box-shadow: 0 0.1em 0.75em var(--colour-page-background);
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: flex-start;
  margin: 0;
  max-width: 300px;
  overflow: hidden;
  padding: 0;
  text-align: left;
  width: 100%;
}

.img {
  aspect-ratio: 3 / 2;
  width: 100%;
  flex-shrink: 0;
}

.card-info {
  padding: 0.5em 1em;
  font-weight: bold;
  color: var(--colour-dark-blue);
  font-size: var(--body);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  gap: 0.2em;
  overflow-wrap: anywhere;
  hyphens: auto;
}

.card-info span {
  color: var(--colour-text-light);
  font-size: var(--small);
  font-weight: normal;
}

.status:after {
  content: ')';
}

.status:before {
  content: '(';
}

.owner {
  margin-bottom: auto;
}

.card-info .guest {
  color: white;
  background-color: var(--colour-offers);
  padding: 2px 4px;
  border-radius: 1em;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  font-size: 12px;
}

/* desktop & tablet */
.desktop .card-info {
  font-size: 1.25em;
}

.desktop .card-info .guest {
  padding: 3px 6px;
  font-size: 14px;
}

.desktop button {
  transition: transform 0.25s ease-out;
}

.desktop button:hover,
.desktop button:focus-visible {
  box-shadow: 0 0 0 7px var(--focus-ring-colour);
  transform: scale(1.05);
  outline: none;
  border-color: var(--focus-ring-colour);
}
</style>
